import { createContext, useContext, useEffect, useState, useRef } from "react";
import axios from "axios";
import { authAxios } from "../utils/auth";

const ThemeContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// Get initial theme synchronously from localStorage to prevent flash
const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'ancient_rome';
  return localStorage.getItem("app_theme") || localStorage.getItem("user_theme") || "ancient_rome";
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize with stored theme to prevent flash
  const [theme, setTheme] = useState(getInitialTheme);
  const [loading, setLoading] = useState(false); // Start as false to prevent loading state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const isChanging = useRef(false);
  const hasFetchedDefault = useRef(false);
  const hasInitialized = useRef(false);

  // Set initial theme on DOM immediately
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, []);

  // Check if user is logged in and fetch admin default only once
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeTheme = async () => {
      const userTheme = localStorage.getItem("user_theme");
      const hasSession = document.cookie.includes("session_token") || localStorage.getItem("eros_refresh_token");
      
      if (hasSession && userTheme) {
        // User is logged in and has a saved theme - already set from getInitialTheme
        setIsLoggedIn(true);
      } else if (!hasSession && !hasFetchedDefault.current) {
        // User is NOT logged in - fetch admin default theme (only once)
        hasFetchedDefault.current = true;
        try {
          const response = await axios.get(`${API}/api/public/default-theme`);
          const adminDefault = response.data.default_theme || "ancient_rome";
          // Only update if different from current
          if (adminDefault !== theme) {
            setTheme(adminDefault);
            localStorage.setItem("app_theme", adminDefault);
            document.documentElement.setAttribute("data-theme", adminDefault);
          }
        } catch (error) {
          // Keep current theme, don't change anything
          console.log("Could not fetch admin default theme");
        }
        setIsLoggedIn(false);
      }
    };

    initializeTheme();
  }, [theme]);

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    
    // Persist to localStorage
    localStorage.setItem("app_theme", theme);
    if (isLoggedIn) {
      localStorage.setItem("user_theme", theme);
    }
  }, [theme, isLoggedIn]);

  const changeTheme = (newTheme) => {
    // Prevent duplicate calls
    if (isChanging.current || newTheme === theme) {
      return;
    }
    
    isChanging.current = true;
    setTheme(newTheme);
    
    setTimeout(() => {
      isChanging.current = false;
    }, 100);
    
    // Persist to user profile in background (only for logged in users)
    if (isLoggedIn) {
      authAxios.put('/profile', { theme: newTheme })
        .catch(error => console.error("Failed to save theme preference:", error));
    }
  };

  // Function to sync theme when user logs in
  const syncUserTheme = (userTheme) => {
    setIsLoggedIn(true);
    if (userTheme && userTheme !== theme) {
      setTheme(userTheme);
      localStorage.setItem("user_theme", userTheme);
      localStorage.setItem("app_theme", userTheme);
    }
  };

  // Function to reset to admin default on logout
  const resetToDefault = async () => {
    setIsLoggedIn(false);
    localStorage.removeItem("user_theme");
    // NOTE: removed localStorage.removeItem("eros_refresh_token") here — this
    // function is a THEME reset, not an auth reset. Deleting the refresh token
    // was silently logging users out on any theme change / logout-triggered
    // reset even when the auth session was meant to persist. Auth logout is
    // handled centrally by utils/auth.js:logout() → clearTokens().
    hasFetchedDefault.current = false;
    
    try {
      const response = await axios.get(`${API}/api/public/default-theme`);
      const adminDefault = response.data.default_theme || "ancient_rome";
      setTheme(adminDefault);
      localStorage.setItem("app_theme", adminDefault);
    } catch (error) {
      setTheme("ancient_rome");
      localStorage.setItem("app_theme", "ancient_rome");
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme, loading, syncUserTheme, resetToDefault }}>
      {children}
    </ThemeContext.Provider>
  );
};
